import React, { useMemo, useRef, useState } from "react";
import { api, GROUP_COLORS } from "../lib/api";
import PrintButton from "./PrintButton";
import ChartContainer from "./ChartContainer";
import SquadIssuesReport from "./SquadIssuesReport";
import HealthRanking from "./HealthRanking";
import SquadWeatherMap from "./SquadWeatherMap";
import SquadSeasonalHeatmap from "./SquadSeasonalHeatmap";
import SecurityWidget from "./SecurityWidget";
import PausedAthletesAlert from "./PausedAthletesAlert";
import { FileText } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList,
} from "recharts";

// Module-level constants — these are stable references so Recharts doesn't
// see "new" props on every render and re-mount its internals.
const CHART_MARGIN = { top: 8, right: 36, left: 24, bottom: 8 };
const X_DOMAIN = [0, 100];
const TICK_MONO = { fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono" };
const TICK_LABEL = { fill: "#CBD5E1", fontSize: 12, fontFamily: "Manrope" };
const TOOLTIP_STYLE = {
  backgroundColor: "#FCD34D",
  border: "1px solid #B45309",
  borderRadius: 10,
  color: "#111827",
  fontSize: 12,
  fontFamily: "JetBrains Mono",
  boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
};
const TOOLTIP_LABEL_STYLE = { color: "#7C2D12", fontWeight: 600 };
const TOOLTIP_ITEM_STYLE = { color: "#111827" };
const BAR_RADIUS = [0, 8, 8, 0];

export default function OverviewPanel({ overview, parameters, onSelectSailor }) {
  const [squadIssuesOpen, setSquadIssuesOpen] = useState(false);
  const topDefRef = useRef(null);

  if (!overview) return null;

  return (
    <div className="space-y-5 fade-up" data-testid="overview-panel">
      {/* Admin-only sign-in security card. Renders as a no-op for
          non-admins — kept at the very top so a spike in failed
          logins is impossible to miss. */}
      <SecurityWidget />

      {/* Paused athletes reminder — the moment an athlete is expected
          back but hasn't returned, this banner turns red and lists
          them so the coach can check in. Renders nothing when no
          athletes are paused. */}
      <PausedAthletesAlert onOpenAthlete={onSelectSailor} />

      {/* Squad Health Weather Map — the fastest triage view. Placed first
          so a coach opening the dashboard sees squad-wide status instantly. */}
      <SquadWeatherMap parameters={parameters} />

      {/* Seasonal Deficiency Heatmap — pattern-finding across time. */}
      <SquadSeasonalHeatmap parameters={parameters} />

      {/* Top deficiencies bar chart */}
      <div className="card p-5 md:p-6" ref={topDefRef}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Top deficiencies</div>
            <div className="font-display text-lg text-slate-100 mt-0.5">
              % of athletes out-of-range on their latest test
            </div>
          </div>
          <div data-no-print="true" className="flex items-center gap-2">
            <button
              className="pill"
              onClick={() => setSquadIssuesOpen(true)}
              data-testid="print-squad-issues-btn"
              title="Open a printable PDF of every athlete's lab deviations"
            >
              <FileText size={13} /> Print squad issues
            </button>
            <PrintButton getNode={() => topDefRef.current} filename="top_deficiencies.png" label="Print" />
          </div>
        </div>
        <div style={{ width: "100%", height: 320, minWidth: 0 }}>
          <ChartContainer>
            <BarChart
              data={overview.top_deficiencies}
              layout="vertical"
              margin={CHART_MARGIN}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#1E304B" horizontal={false} />
              <XAxis
                type="number"
                domain={X_DOMAIN}
                axisLine={false}
                tickLine={false}
                tick={TICK_MONO}
                unit="%"
              />
              <YAxis
                type="category"
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={TICK_LABEL}
                width={150}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                itemStyle={TOOLTIP_ITEM_STYLE}
                formatter={(v, _n, props) => [`${v}%  (${props.payload.out_of_range}/${props.payload.tested})`, "Out of range"]}
              />
              <Bar dataKey="pct" radius={BAR_RADIUS} animationDuration={700}>
                {overview.top_deficiencies.map((d) => {
                  const hue = d.pct >= 70 ? "#EF4444" : d.pct >= 45 ? "#F59E0B" : "#00E5FF";
                  return <Cell key={d.key} fill={hue} />;
                })}
                <LabelList dataKey="pct" position="right" fill="#F1F5F9" fontSize={11} fontFamily="JetBrains Mono"
                  formatter={(v) => `${v}%`} />
              </Bar>
            </BarChart>
          </ChartContainer>
        </div>
      </div>

      {/* Health ranking — healthiest → needs attention */}
      <HealthRanking onSelectSailor={onSelectSailor} />

      <SquadIssuesReport
        open={squadIssuesOpen}
        onClose={() => setSquadIssuesOpen(false)}
      />
    </div>
  );
}
